// File: src/components/layout/UserMenu.tsx
import React from 'react';
import { motion } from 'framer-motion';
import { useNavigate } from 'react-router-dom';
import { User, Settings, Crown, LogOut, CreditCard } from 'lucide-react';
import { useAuthStore } from '@/stores/authStore';
import { useFeatureAccess } from '@/components/auth/ProtectedRoute';

interface UserMenuProps {
  onClose: () => void;
}

export const UserMenu: React.FC<UserMenuProps> = ({ onClose }) => {
  const navigate = useNavigate();
  const { userProfile, signOut } = useAuthStore();
  const { subscription } = useFeatureAccess();

  const handleSignOut = async () => {
    try {
      await signOut();
      navigate('/auth/signin');
      onClose();
    } catch (error) {
      console.error('Sign out failed:', error);
    }
  };

  const handleNavigation = (path: string) => {
    navigate(path);
    onClose();
  };

  const menuItems = [
    {
      icon: User,
      label: 'Profile',
      onClick: () => handleNavigation('/settings/profile'),
    },
    {
      icon: Settings,
      label: 'Settings',
      onClick: () => handleNavigation('/settings'),
    },
  ];

  // Add subscription-related items
  if (subscription?.tier !== 'free') {
    menuItems.push({
      icon: CreditCard,
      label: 'Billing',
      onClick: () => handleNavigation('/settings/billing'),
    });
  }

  return (
    <motion.div
      initial={{ opacity: 0, scale: 0.95 }}
      animate={{ opacity: 1, scale: 1 }}
      exit={{ opacity: 0, scale: 0.95 }}
      className="absolute right-0 top-full mt-2 w-64 bg-white dark:bg-gray-800 rounded-lg shadow-lg border border-gray-200 dark:border-gray-700 py-2 z-50"
    >
      {/* User Info */}
      <div className="px-4 py-3 border-b border-gray-200 dark:border-gray-700">
        <div className="flex items-center space-x-3">
          {userProfile?.picture ? (
            <img
              src={userProfile.picture}
              alt={userProfile.name || 'User'}
              className="w-10 h-10 rounded-full"
            />
          ) : (
            <div className="w-10 h-10 bg-gray-200 dark:bg-gray-700 rounded-full flex items-center justify-center">
              <User className="w-5 h-5 text-gray-600 dark:text-gray-400" />
            </div>
          )}
          <div className="flex-1 min-w-0">
            <p className="text-sm font-medium text-gray-900 dark:text-white truncate">
              {userProfile?.name || userProfile?.email}
            </p>
            <p className="text-xs text-gray-500 dark:text-gray-400 truncate">
              {userProfile?.email}
            </p>
          </div>
        </div>
        
        {/* Subscription Badge */}
        <div className="mt-2 flex items-center justify-between">
          <div className="flex items-center space-x-2">
            {subscription?.tier !== 'free' && (
              <Crown className="w-4 h-4 text-yellow-500" />
            )}
            <span className="text-xs font-medium px-2 py-1 rounded-full bg-gray-100 dark:bg-gray-700 text-gray-700 dark:text-gray-300">
              {subscription?.tier ? subscription.tier.charAt(0).toUpperCase() + subscription.tier.slice(1) : 'Free'} Plan
            </span>
          </div>
        </div>
      </div>

      {/* Menu Items */}
      <div className="py-1">
        {menuItems.map((item, index) => {
          const Icon = item.icon;
          return (
            <button
              key={index}
              onClick={item.onClick}
              className="w-full flex items-center space-x-3 px-4 py-2 text-sm text-gray-700 dark:text-gray-300 hover:bg-gray-100 dark:hover:bg-gray-700 transition-colors"
            >
              <Icon className="w-4 h-4" />
              <span>{item.label}</span>
            </button>
          );
        })}
      </div>

      {/* Upgrade CTA for Free Users */}
      {subscription?.tier === 'free' && (
        <div className="px-4 py-3 border-t border-gray-200 dark:border-gray-700">
          <button
            onClick={() => handleNavigation('/upgrade')}
            className="w-full flex items-center justify-center space-x-2 px-3 py-2 bg-gradient-to-r from-purple-600 to-blue-600 text-white rounded-lg text-sm font-medium hover:from-purple-700 hover:to-blue-700 transition-colors"
          >
            <Crown className="w-4 h-4" />
            <span>Upgrade to Pro</span>
          </button>
        </div>
      )}

      {/* Sign Out */}
      <div className="border-t border-gray-200 dark:border-gray-700 py-1">
        <button
          onClick={handleSignOut}
          className="w-full flex items-center space-x-3 px-4 py-2 text-sm text-red-600 dark:text-red-400 hover:bg-red-50 dark:hover:bg-red-900/20 transition-colors"
        >
          <LogOut className="w-4 h-4" />
          <span>Sign Out</span>
        </button>
      </div>
    </motion.div>
  );
};